import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import './api-docs.style.css';

type Operation = {
  tags?: string[];
  summary?: string;
  ['x-auth-modes']?: string[];
};
type PathItem = Record<string, Operation>;
type TagDef = { name: string; description?: string; ['x-audience']?: 'Integration' | 'Internal Portal' };
type OpenApiSpec = {
  paths?: Record<string, PathItem>;
  tags?: TagDef[];
  [k: string]: unknown;
};

type Audience = 'Integration' | 'Internal Portal';

const AUTH_MODE_META = {
  SuperAdmin: {
    label: 'Super Admin',
    color: '#c62828',
    bg: '#ffebee',
    description:
      'Firebase ID token from a signed-in Super Admin. Used by the internal SalesRook admin portal.'
  },
  AdminUser: {
    label: 'Admin / User',
    color: '#1565c0',
    bg: '#e3f2fd',
    description:
      'Firebase ID token from any authenticated non-SuperAdmin role (Admin, Agent, or User). Used by the admin portal and the negotiator extension.'
  },
  ExternalKey: {
    label: 'External Key',
    color: '#2e7d32',
    bg: '#e8f5e9',
    description:
      'Server-to-server. Accepts a UserApi Bearer JWT (issued via POST /api-key) OR, for legacy webhook endpoints, a Bearer JWT signed with WH_SECRET.'
  },
  Public: {
    label: 'Public',
    color: '#616161',
    bg: '#f5f5f5',
    description:
      'No authentication middleware detected. Typically login flows and unauthenticated third-party webhook receivers.'
  }
} as const;

type AuthMode = keyof typeof AUTH_MODE_META;
const AUTH_MODES: AuthMode[] = ['SuperAdmin', 'AdminUser', 'ExternalKey', 'Public'];

const AUDIENCE_META: Record<Audience, { label: string; color: string; description: string }> = {
  Integration: {
    label: 'Integration APIs',
    color: '#2e7d32',
    description:
      'Endpoints external systems and third-party integrations are meant to call — anything a UserApi customer or signed webhook sender can reach.'
  },
  'Internal Portal': {
    label: 'Internal Portal APIs',
    color: '#1565c0',
    description:
      'Endpoints reserved for the admin portal, super-admin portal, and negotiator Chrome extension. Not intended for external integrators.'
  }
};

const VERB_ORDER: Record<string, number> = {
  post: 1, // create
  get: 2, // read (list / detail)
  put: 3, // full update
  patch: 4, // partial update
  delete: 5,
  head: 6,
  options: 7
};

const opsSorter = (a: { get: (k: string) => string }, b: { get: (k: string) => string }): number => {
  const av = VERB_ORDER[a.get('method')] ?? 99;
  const bv = VERB_ORDER[b.get('method')] ?? 99;
  if (av !== bv) return av - bv;
  return a.get('path').localeCompare(b.get('path'));
};

const tagsSorter = (a: string, b: string): number => a.localeCompare(b);

const ApiDocsPage: React.FC<RouteComponentProps> = () => {
  const [spec, setSpec] = useState<OpenApiSpec | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audience, setAudience] = useState<Audience | null>(null);

  useEffect(() => {
    const base = process.env.REACT_APP_API_BASE;
    fetch(`${base}/swagger.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<OpenApiSpec>;
      })
      .then(setSpec)
      .catch((e: Error) => setError(e.message ?? String(e)));
  }, []);

  const tagAudience = useMemo(() => {
    const map: Record<string, Audience> = {};
    for (const t of spec?.tags ?? []) {
      if (t['x-audience']) map[t.name] = t['x-audience'];
    }
    return map;
  }, [spec]);

  const audienceCounts = useMemo<Record<Audience, number>>(() => {
    const acc: Record<Audience, number> = { Integration: 0, 'Internal Portal': 0 };
    for (const methods of Object.values(spec?.paths ?? {})) {
      for (const op of Object.values(methods)) {
        for (const t of op.tags ?? []) {
          const a = tagAudience[t];
          if (a) {
            acc[a]++;
            break;
          }
        }
      }
    }
    return acc;
  }, [spec, tagAudience]);

  const authModeCounts = useMemo<Record<AuthMode, number>>(() => {
    const acc: Record<AuthMode, number> = { SuperAdmin: 0, AdminUser: 0, ExternalKey: 0, Public: 0 };
    for (const methods of Object.values(spec?.paths ?? {})) {
      for (const op of Object.values(methods)) {
        for (const m of op['x-auth-modes'] ?? []) {
          if (m in acc) acc[m as AuthMode]++;
        }
      }
    }
    return acc;
  }, [spec]);

  const filteredSpec = useMemo<OpenApiSpec | null>(() => {
    if (!spec || !audience) return spec;
    const paths: Record<string, PathItem> = {};
    for (const [p, methods] of Object.entries(spec.paths ?? {})) {
      const kept: PathItem = {};
      for (const [m, op] of Object.entries(methods)) {
        const opAudience = (op.tags ?? []).map((t) => tagAudience[t]).find(Boolean);
        if (opAudience === audience) kept[m] = op;
      }
      if (Object.keys(kept).length) paths[p] = kept;
    }
    const tags = (spec.tags ?? []).filter((t) => t['x-audience'] === audience);
    return { ...spec, paths, tags };
  }, [spec, audience, tagAudience]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        SalesRook API Documentation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Endpoints are grouped by resource. Each operation carries an auth marker in its title
        (e.g. <code>[Super Admin]</code>, <code>[External Key]</code>) and the native OpenAPI
        security block below shows which schemes are accepted. Use the audience filter to
        narrow the view to integration-facing endpoints only.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {AUTH_MODES.map((mode) => {
          const meta = AUTH_MODE_META[mode];
          return (
            <Paper
              key={mode}
              variant="outlined"
              sx={{
                p: 1.5,
                minWidth: 220,
                borderLeft: `4px solid ${meta.color}`,
                bgcolor: meta.bg
              }}
            >
              <Typography variant="subtitle2" sx={{ color: meta.color, fontWeight: 700 }}>
                {meta.label}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                {authModeCounts[mode]} operation{authModeCounts[mode] === 1 ? '' : 's'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {meta.description}
              </Typography>
            </Paper>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary', fontWeight: 600 }}>
          Audience:
        </Typography>
        <Chip
          label="All"
          onClick={() => setAudience(null)}
          variant={audience === null ? 'filled' : 'outlined'}
          color="default"
        />
        {(Object.keys(AUDIENCE_META) as Audience[]).map((a) => {
          const meta = AUDIENCE_META[a];
          const active = audience === a;
          return (
            <Chip
              key={a}
              label={`${meta.label} · ${audienceCounts[a]}`}
              onClick={() => setAudience(active ? null : a)}
              variant={active ? 'filled' : 'outlined'}
              sx={{
                borderColor: meta.color,
                color: active ? '#fff' : meta.color,
                bgcolor: active ? meta.color : 'transparent',
                '&:hover': { bgcolor: active ? meta.color : `${meta.color}22` }
              }}
              title={meta.description}
            />
          );
        })}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load the OpenAPI spec from <code>{process.env.REACT_APP_API_BASE}/swagger.json</code>: {error}
        </Alert>
      )}
      {!spec && !error && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} /> Loading OpenAPI spec…
        </Box>
      )}
      {filteredSpec && (
        <Box className="sr-swagger-container">
          {React.createElement(
            SwaggerUI as unknown as React.ComponentType<Record<string, unknown>>,
            {
              key: audience ?? 'all',
              spec: filteredSpec,
              docExpansion: 'list',
              defaultModelsExpandDepth: -1,
              operationsSorter: opsSorter,
              tagsSorter,
              tryItOutEnabled: true
            }
          )}
        </Box>
      )}
    </Box>
  );
};

export default ApiDocsPage;
